import React, { useState } from 'react';
import { googleListingApi, GoogleListingCandidate } from '../../services/googleListing';
import { getApiErrorMessage } from '../../utils/errors';
import GoogleMapsAttribution from './GoogleMapsAttribution';

interface GoogleListingPickerProps {
  placeId: string | null | undefined;
  onChange?: (placeId: string | null) => void;
}

// Lets a provider pick their own Google listing. Only the listing's ID is saved; the
// rating is fetched live from Google when the profile is viewed.
const GoogleListingPicker: React.FC<GoogleListingPickerProps> = ({ placeId, onChange }) => {
  const [linkedId, setLinkedId] = useState<string | null>(placeId ?? null);
  const [searching, setSearching] = useState(!placeId);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GoogleListingCandidate[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (next: string | null) => {
    setLinkedId(next);
    onChange?.(next);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim().length < 3) {
      setError('Enter at least 3 characters, such as your practice name and city.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setResults(await googleListingApi.search(query.trim()));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not search Google listings.'));
    } finally {
      setBusy(false);
    }
  };

  const handleLink = async (candidate: GoogleListingCandidate) => {
    setBusy(true);
    setError(null);
    try {
      const { google_place_id } = await googleListingApi.link(candidate.place_id);
      update(google_place_id);
      setSearching(false);
      setResults(null);
      setQuery('');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not link that listing.'));
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    setBusy(true);
    setError(null);
    try {
      await googleListingApi.unlink();
      update(null);
      setSearching(true);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not remove the listing.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="font-['Open_Sans'] text-sm text-[#1f1f1f]">
      <p className="text-[#767472] mb-3">
        Link your practice's Google listing to show its Google rating on your profile. We save only
        the listing's ID. The rating comes live from Google and isn't part of your CLOUT Score.
      </p>

      {linkedId && !searching && (
        <div className="flex flex-wrap items-center gap-3">
          <span>Your Google listing is linked.</span>
          <button
            type="button"
            onClick={() => setSearching(true)}
            disabled={busy}
            className="text-[#c6a664] hover:underline disabled:opacity-50"
          >
            Change
          </button>
          <button
            type="button"
            onClick={handleUnlink}
            disabled={busy}
            className="text-[#c6a664] hover:underline disabled:opacity-50"
          >
            Remove
          </button>
        </div>
      )}

      {searching && (
        <>
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Practice name and city"
              aria-label="Search Google listings"
              className="flex-1 px-4 py-2.5 border border-[#e5e5ea] rounded focus:outline-none focus:border-[#c6a664] text-base"
            />
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-2.5 bg-[#c6a664] text-white font-semibold uppercase text-xs hover:bg-[#b89854] disabled:opacity-50"
            >
              Search
            </button>
            {linkedId && (
              <button
                type="button"
                onClick={() => {
                  setSearching(false);
                  setResults(null);
                }}
                className="px-4 py-2.5 text-[#767472] hover:underline"
              >
                Cancel
              </button>
            )}
          </form>

          {results && (
            <div className="mt-3 border border-[#dadce0] rounded-lg px-[10px] pt-[10px] pb-2">
              <GoogleMapsAttribution />
              {results.length === 0 ? (
                <p className="mt-2 text-[#5e5e5e]">No listings found. Try another search.</p>
              ) : (
                <ul className="mt-2 divide-y divide-[#eaeaea]">
                  {results.map((candidate) => (
                    <li
                      key={candidate.place_id}
                      className="py-2 flex items-center justify-between gap-3"
                    >
                      <div>
                        <p className="font-semibold">{candidate.name}</p>
                        {candidate.address && (
                          <p className="text-xs text-[#5e5e5e]">{candidate.address}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleLink(candidate)}
                        disabled={busy}
                        className="shrink-0 px-3 py-1.5 border border-[#c6a664] text-[#c6a664] text-xs font-semibold uppercase hover:bg-[#c6a664] hover:text-white disabled:opacity-50"
                      >
                        This is mine
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      {error && (
        <p role="alert" className="mt-2 text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export default GoogleListingPicker;
